import { setup } from 'goober';
import { MessageCircle, X } from 'lucide-react';
import { useEffect, useState } from 'preact/hooks';
import { createElement } from 'react';
import AgentChatbotPC from './AgentChatbotRC/AgentChatbotPC';
import { http } from './AgentChatbotRC/http';
import {
    AgentChatbotWindow,
    AgentChatbotWindowExpand,
    AgentToggleBtn,
    AgentWapper,
    ChatbotWrapper
} from './AgentChatbotRC/styles';
import type { IAgentItem } from './AgentChatbotRC/type';

// Initialize goober for this component only
setup(createElement);

// Mock agent data for development
const mockAgentInfo: IAgentItem = {
    _id: 'dev-agent',
    user: 'dev-user',
    name: 'Development Agent',
    slug: 'dev-agent',
    model: 'gpt-3.5-turbo',
    temperature: 0.7,
    status: 'active',
    settings: {
        brand_url: '',
        placeholder_message: 'Type your message...',
        suggestion_messages: [
            { message: 'Hello! How can I help you?' },
            { message: 'Tell me more about your services' }
        ],
        theme: 'light',
        welcome_message: 'Welcome to Development Chat!',
        header_bg_color: '#000000',
        message_bg_color: '#007bff',
        chatbot_width: '400px',
        border_radius: '8px',
        api_type: 'openai'
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    __v: 0
};

interface IProps {
    apiKey: string;
}

const AgentChatbotWidget = ({ apiKey }: IProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [agentInfo, setAgentInfo] = useState<IAgentItem>();
    const [isExpand, setIsExpand] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const isDevelopment =
        import.meta.env.VITE_APP_BASE_URL?.includes('localhost') ||
        import.meta.env.MODE === 'development';

    // Use a development key if provided and no API key is passed
    const effectiveApiKey =
        apiKey || (isDevelopment ? import.meta.env.VITE_DEV_API_KEY : null);

    const onFetchAgentInfo = async () => {
        // If no API key in development, use mock data immediately
        if (isDevelopment && !effectiveApiKey) {
            console.log(
                'Development mode: No API key provided, using mock agent data'
            );
            setAgentInfo(mockAgentInfo);
            setIsLoading(false);
            return;
        }

        try {
            const res = await http.get<IAgentItem>(
                `/agents/${effectiveApiKey}`
            );
            setAgentInfo(res);
        } catch (_error) {
            if (!isDevelopment) {
                console.error('Error: Invalid BoostBuddy API key');
            } else {
                console.log(
                    'Development mode: API call failed, using mock agent data'
                );
                // Use mock data in development
                setAgentInfo(mockAgentInfo);
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        onFetchAgentInfo()
            .then(() => {})
            .catch(() => {});
    }, []);

    useEffect(() => {
        const originalOverflow = document.body.style.overflow;

        if (isExpand) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = originalOverflow || 'auto';
        }

        return () => {
            // Reset on unmount
            document.body.style.overflow = originalOverflow;
        };
    }, [isExpand]);

    if (!agentInfo && !isLoading && !isDevelopment) {
        return null; // Don't render the widget if agent info is not available in production
    }

    return (
        <AgentWapper>
            <AgentToggleBtn
                onClick={() => setIsOpen(!isOpen)}
                position='right'
                isOpen={isOpen}
            >
                {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
            </AgentToggleBtn>

            <div className={`${isExpand && ChatbotWrapper}`}>
                <AgentChatbotWindow
                    className={`${isExpand && AgentChatbotWindowExpand}`}
                    position='right'
                    style={{
                        display: isOpen ? 'flex' : 'none',
                        position: isExpand ? 'static' : 'fixed',
                        width: isExpand ? '60%' : 'var(--bb-popup-width)'
                    }}
                >
                    <AgentChatbotPC
                        onChangeModel={(v: boolean) => {
                            setIsOpen(v);
                            setIsExpand(false);
                        }}
                        isOpen={isOpen}
                        agentInfo={agentInfo || mockAgentInfo}
                        onChangeExpand={() => {
                            setIsExpand(!isExpand);
                        }}
                    />
                </AgentChatbotWindow>
            </div>
        </AgentWapper>
    );
};

export default AgentChatbotWidget;
